import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { ROUTES } from '../constants/routes';
import { UI, DISPLAY, EASE } from '../styles/tl5';
import {
  useListAboutTimelineQuery,
  useListAboutNumbersQuery,
  useListAboutTeamQuery,
  useGetContactInfoQuery,
  useGetPageSectionsQuery,
} from '../store/siteContentApi';
// The reference's own three photographs, lifted out of its bundle.
// `about-founder.png` is a 376x626 RGBA cut-out, which is why the
// reference mats it on black and uses object-fit: contain.
import heroImg from '../assets/img/v5/about-hero.jpg';
import founderImg from '../assets/img/v5/about-founder.png';
import centreImg from '../assets/img/v5/about-centre.jpg';

// About — built to the v5 reference, and only what the reference has.
//
// Eleven sections: hero, the four numbers, our story, vision + mission,
// the journey, what we stand for, departments, quality policy,
// accreditations, leadership, our network, and the two contact cards.
//
// Three things the old page carried that the reference does not, and so
// are not here any more:
//   · the advisory-board grid (`useListAboutAdvisory`)
//   · the partnership enquiry form
//   · the Trustian application form
// The two forms are lead capture and should not simply vanish — in the v5
// information architecture they belong on Partners and on Careers, both of
// which are still to be built. Noted in API-NOTES.md so they are not lost.
//
// Still admin-driven: the numbers, the journey, the leadership list and
// the contact details.
//
// The rest of the copy — the story, what we stand for, the departments,
// the quality policy, the accreditations and the network — now comes
// from `GET /api/page-sections/about`. Those are claims about the lab
// (ISO 15189, the EQAS pass rate, which departments exist) and must be
// correctable without a deploy. The constants below stay as the
// fallback and must keep matching the controller's DEFAULTS.about.

const DEPARTMENTS = [
  { n: 'Biochemistry', lead: true },
  { n: 'Haematology' },
  { n: 'Clinical Pathology' },
  { n: 'Microbiology' },
  { n: 'Serology' },
  { n: 'Molecular Biology', lead: true },
  { n: 'Flow Cytometry' },
  { n: 'Histopathology' },
  { n: 'Cytology' },
  { n: 'Cytogenetics' },
  { n: 'Prenatal & Newborn Screening', lead: true },
  { n: 'Genomics' },
];

const STANDS_FOR = [
  {
    t: 'Accuracy you can check',
    d: 'NABL accredited to ISO 15189:2022 and tested every year through external quality assessment.',
  },
  {
    t: 'A doctor reads every report',
    d: 'Results are verified by a qualified doctor before they reach you or your physician.',
  },
  {
    t: 'Speed without shortcuts',
    d: 'Trustians at your door in 60 minutes and most routine reports in about 6 hours.',
  },
  {
    t: 'Specialists close to home',
    d: 'Genomics and specialty testing in-house, so complex samples don’t have to travel far.',
    accent: true,
  },
];

const QUALITY = [
  ['Accurate results', 'Validated methods, calibrated equipment and daily internal quality control.'],
  ['Competent people', 'Qualified pathologists and trained technologists, assessed and trained continually.'],
  ['Impartial and confidential', 'Every result is decided on evidence alone, free from commercial or other pressure, and patient information is protected at every step.'],
  ['Patient safety', 'Right patient, right sample, right result, with critical results communicated without delay.'],
  ['Clear and on time', 'Reports released within the committed time, in language patients and doctors can understand.'],
  ['Checked independently', 'External quality assessment (EQAS), internal audits and NABL assessments.'],
  ['Always improving', 'Complaints, incidents and audit findings drive corrective action.'],
];

const ACCREDITATIONS = [
  ['NABL certificate', 'NABL · ISO 15189:2022', 'The international standard for quality and competence in medical laboratories.'],
  ['ICMR approval letter', 'ICMR approved', 'Approved by the Indian Council of Medical Research for COVID-19 RT-PCR testing.'],
  ['EQAS report', 'External quality assessment', '98.4% pass rate in FY26 across our external proficiency testing programmes.'],
];

// Fallback — mirrors DEFAULTS.about.story.paragraphs.
const STORY_PARAGRAPHS = [
              'In 2020, as COVID-19 overwhelmed India’s healthcare system, our Chairman and Managing Director, Venkata Suman Cherukuri, set out to build a laboratory that doctors and patients could trust without second-guessing: a central clinical reference and genomics research lab, combining current technology with global best practice.',
              'We began in January 2021 with a regional lab in Vijayawada. Within three months we were running biochemistry and molecular biology tests, received ICMR approval for RT-PCR testing, and earned NABL accreditation for molecular biology.',
              'From there we kept adding what doctors asked us for: microbiology, serology and haematology, then flow cytometry, prenatal and newborn screening, histopathology and cytogenetics. Our aim is a centre of excellence for specialised testing and advanced genomics, close to the patients who need it.',
];

const REGIONS = [
  'Telangana', 'Andhra Pradesh', 'Karnataka',
  'Jammu & Kashmir', 'Punjab / Chandigarh', 'Delhi NCR',
];

// Used until the admin has filled /about/numbers and /about/timeline.
const FALLBACK_NUMBERS = [
  { value: '98.4%', title: 'EQAS pass rate, FY26' },
  { value: '1,765', title: 'Active reportable tests' },
  { value: '13', title: 'Departments' },
  { value: '14', title: 'Branches across India' },
];

const FALLBACK_TIMELINE = [
  { year: 'Aug 2020', title: 'TrustLab Diagnostics is founded', description: 'Incorporated in Hyderabad while COVID-19 was stretching India’s testing capacity. The goal: a central clinical reference and genomics lab that doctors could rely on.' },
  { year: 'Apr 2021', title: 'ICMR approval and NABL accreditation', description: 'Approved by ICMR for COVID-19 RT-PCR testing, and accredited by NABL as a molecular biology laboratory.' },
  { year: 'Jun 2021', title: '100 network partners', description: 'Hospitals, clinics and labs began referring samples. Microbiology, serology and haematology departments opened.' },
  { year: '2021–22', title: 'Specialty testing grows', description: 'Flow cytometry, prenatal and newborn screening, histopathology and cytogenetics were added. Regional labs opened in Bengaluru and Aurangabad, a phlebotomy network launched in five cities, and 300 more partners joined.' },
  { year: '2023', title: 'Expands into North India', description: 'Operations in Noida, Chandigarh and Jammu.' },
  { year: '2025', title: 'A national network', description: 'NABL accredited to ISO 15189:2022, 1,765 reportable tests across 13 departments, 14 branches, and home collection by our Trustians.' },
];

// ── shared fragments ──────────────────────────────────────────────────
const SECTION_TOP = { mt: 6 };

const panel = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  minWidth: 0,
  p: 'clamp(22px, 3.4vw, 36px)',
  borderRadius: '28px',
  bgcolor: '#FFFFFF',
};

const eyebrow = {
  fontFamily: UI,
  fontSize: 13,
  fontWeight: 800,
  letterSpacing: '.08em',
  textTransform: 'uppercase',
  color: 'var(--tl-teal-d)',
};

const h2 = {
  m: 0,
  fontFamily: DISPLAY,
  fontWeight: 700,
  fontSize: 'clamp(28px, 3.8vw, 48px)',
  lineHeight: 1.02,
  letterSpacing: '-.03em',
  color: 'var(--tl-g9)',
};

const cardTitle = {
  fontFamily: DISPLAY,
  fontWeight: 700,
  fontSize: 22,
  lineHeight: 1.15,
  letterSpacing: '-.01em',
};

const About = () => {
  const { data: numbers = [] } = useListAboutNumbersQuery();
  const { data: timeline = [] } = useListAboutTimelineQuery();
  const { data: team = [] } = useListAboutTeamQuery();
  const { data: contact } = useGetContactInfoQuery();
  const { data: sections } = useGetPageSectionsQuery('about');

  // Each block falls back to the constant above it, so a slow or failing
  // call renders exactly what shipped.
  const sec = sections || {};
  const hero = sec.hero || {};
  const story = sec.story || {};
  const qp = sec.quality_policy || {};
  const standsFor = sec.stands_for?.items?.length ? sec.stands_for.items : STANDS_FOR;
  const departments = sec.departments?.items?.length ? sec.departments.items : DEPARTMENTS;
  const quality = sec.quality_policy?.items?.length
    ? sec.quality_policy.items
    : QUALITY.map(([t, d]) => ({ t, d }));
  const accreditations = sec.accreditations?.items?.length
    ? sec.accreditations.items
    : ACCREDITATIONS.map(([doc, t, d]) => ({ doc, t, d }));
  const regions = sec.network?.regions?.length ? sec.network.regions : REGIONS;
  const storyParas = story.paragraphs?.length ? story.paragraphs : STORY_PARAGRAPHS;
  // `??`, not `||`: an admin who clears a pill wants it gone, not reset.
  const heroBadge = hero.badge ?? 'About TrustLab \u00b7 Since 2020';
  const qualityBadge = qp.badge ?? 'NABL \u00b7 ISO 15189:2022';

  // The reference shows exactly four. Hero stats first, since that is what
  // the admin flag is for, then whatever else is published.
  const stats = (numbers.length
    ? [...numbers].sort((a, b) => (b.is_hero_stat === true) - (a.is_hero_stat === true))
    : FALLBACK_NUMBERS
  ).slice(0, 4);

  const journey = timeline.length ? timeline : FALLBACK_TIMELINE;
  // The record's photo field is `avatar_url` (not photo_url — that was
  // reading undefined and silently falling back to the initials plate).
  // `sort_order` arrives as a string, hence Number().
  const leaders = [...team]
    .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0))
    .map((m) => ({ ...m, photo: m.avatar_url || m.photo_url || '' }));
  const email = contact?.email || 'admin@mytrustlab.com';
  const address = contact?.address || 'Street #5, Prakash Nagar, Begumpet, Hyderabad, Telangana';
  const cmd = leaders[0] || null;
  // The section wins over the leadership record: it is what an admin
  // edits for this panel specifically.
  const cmdName = story.cmd_name || cmd?.name || 'Venkata Suman Cherukuri';
  const cmdRole = story.cmd_role || cmd?.role || 'Chairman & Managing Director';

  return (
    <Box>
      {/* ── 1. hero ──────────────────────────────────────────────────── */}
      <Box component="section" sx={{ pt: 2, px: 2 }}>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            maxWidth: 1320,
            mx: 'auto',
            minHeight: { xs: 380, md: 520 },
            display: 'flex',
            alignItems: 'end',
            borderRadius: '36px',
            overflow: 'hidden',
            bgcolor: 'var(--tl-g9)',
          }}
        >
          <Box
            component="img"
            src={heroImg}
            alt={hero.image_alt || ''}
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <Box
            aria-hidden="true"
            sx={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(0deg, rgba(7,33,29,0.95) 0%, rgba(7,33,29,0.6) 55%, rgba(7,33,29,0.2) 100%)',
            }}
          />
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              gap: 2.25,
              maxWidth: 940,
              p: 'clamp(28px, 5vw, 56px)',
              color: '#FFFFFF',
            }}
          >
            {heroBadge ? (
              <Box
                component="span"
                sx={{
                  alignSelf: 'start',
                  px: '12px',
                  py: '7px',
                  borderRadius: '999px',
                  bgcolor: 'var(--tl-yellow)',
                  color: 'var(--tl-g9)',
                  fontFamily: UI,
                  fontSize: 13,
                  fontWeight: 800,
                }}
              >
                {heroBadge}
              </Box>
            ) : null}
            <Typography
              component="h1"
              sx={{
                m: 0,
                fontFamily: DISPLAY,
                fontWeight: 700,
                fontSize: 'clamp(34px, 5.4vw, 68px)',
                lineHeight: 0.95,
                letterSpacing: '-.035em',
                textWrap: 'balance',
                color: '#FFFFFF',
              }}
            >
              {hero.headline || 'Started in a crisis.'}{' '}
              <Box component="span" sx={{ color: 'var(--tl-teal-l)' }}>
                {hero.headline_accent || 'Built on trust.'}
              </Box>
            </Typography>
            <Typography
              sx={{ m: 0, fontFamily: UI, fontSize: 18, lineHeight: 1.6, color: '#D5E7E2', maxWidth: 680 }}
            >
              {hero.lede
                || 'TrustLab is a central clinical reference and genomics laboratory headquartered in Hyderabad. We were founded during the COVID-19 pandemic, when families and doctors needed test results they could depend on, fast.'}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        component="main"
        sx={{
          width: '100%',
          maxWidth: 1320,
          mx: 'auto',
          px: 2,
          pt: 1.5,
          pb: 9,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* ── 2. the numbers ─────────────────────────────────────────── */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))',
            gap: 1.5,
          }}
        >
          {stats.map((s) => (
            <Box key={s.title} sx={{ ...panel, gap: 1 }}>
              <Box component="span" sx={{ fontFamily: UI, fontSize: 14, fontWeight: 700, color: 'var(--tl-muted-2)' }}>
                {s.title}
              </Box>
              <Box
                component="span"
                sx={{
                  fontFamily: DISPLAY,
                  fontSize: 'clamp(40px, 5vw, 60px)',
                  fontWeight: 700,
                  letterSpacing: '-.04em',
                  lineHeight: 1,
                  color: 'var(--tl-teal-d)',
                }}
              >
                {s.value}
              </Box>
            </Box>
          ))}
        </Box>

        {/* ── 3. our story ───────────────────────────────────────────── */}
        <Box component="section" sx={{ ...SECTION_TOP, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'stretch' }}>
          <Box sx={{ ...panel, flex: '2 1 520px', gap: 2.25 }}>
            <Box component="span" sx={eyebrow}>{story.eyebrow || 'Our story'}</Box>
            <Typography component="h2" sx={h2}>{story.heading || 'Why we started TrustLab'}</Typography>
            {storyParas.map((p) => (
              <Typography key={p.slice(0, 24)} sx={{ m: 0, fontFamily: UI, fontSize: 17, lineHeight: 1.7, color: '#3E5550' }}>
                {p}
              </Typography>
            ))}
          </Box>

          <Box
            sx={{
              flex: '1 1 320px',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: '28px',
              overflow: 'hidden',
              bgcolor: 'var(--tl-g9)',
              color: '#FFFFFF',
            }}
          >
            {/* The portrait is whatever the admin has on the leadership
                record; without one the plate stands in, as the reference
                does for its own unphotographed people. */}
            <Box
              component="img"
              src={story.portrait_url || founderImg}
              alt={cmdName}
              sx={{
                width: '100%',
                height: { xs: 360, md: 460 },
                objectFit: 'contain',
                objectPosition: 'center bottom',
                display: 'block',
                bgcolor: '#000000',
              }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, flex: 1, p: 'clamp(22px, 3vw, 32px)' }}>
              <Box component="span" sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 44, lineHeight: 0.6, color: 'var(--tl-teal-l)' }}>
                &ldquo;
              </Box>
              <Box
                component="span"
                sx={{
                  fontFamily: DISPLAY,
                  fontWeight: 700,
                  fontSize: 'clamp(24px, 2.6vw, 32px)',
                  lineHeight: 1.1,
                  letterSpacing: '-.02em',
                  textWrap: 'balance',
                }}
              >
                {story.quote || 'Catch what matters, before it matters more.'}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', mt: 'auto' }}>
                <Box component="span" sx={{ fontFamily: UI, fontSize: 16, fontWeight: 800 }}>
                  {cmdName}
                </Box>
                <Box component="span" sx={{ fontFamily: UI, fontSize: 14, color: '#9FC2BA' }}>
                  {cmdRole}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* ── 4. vision + mission ────────────────────────────────────── */}
        <Box
          component="section"
          sx={{
            ...SECTION_TOP,
            display: 'flex',
            flexDirection: 'column',
            gap: 'clamp(24px, 3vw, 36px)',
            p: 'clamp(28px, 5vw, 64px)',
            borderRadius: '36px',
            bgcolor: 'var(--tl-teal)',
            color: '#FFFFFF',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, maxWidth: 980 }}>
            <Box component="span" sx={{ ...eyebrow, color: '#DFFBF4' }}>{story.vision_eyebrow || 'Our vision'}</Box>
            <Typography
              sx={{
                m: 0,
                fontFamily: DISPLAY,
                fontWeight: 700,
                fontSize: 'clamp(30px, 4.6vw, 60px)',
                lineHeight: 1.02,
                letterSpacing: '-.03em',
                textWrap: 'balance',
                color: '#FFFFFF',
              }}
            >
              {story.vision
                || 'To empower people and protect the lives they love, with world-class diagnostics within reach of every community.'}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.25,
              maxWidth: 760,
              pt: 'clamp(20px, 2.6vw, 28px)',
              borderTop: '1px solid #5FCDB7',
            }}
          >
            <Box component="span" sx={{ ...eyebrow, color: 'var(--tl-g9)' }}>{story.mission_eyebrow || 'Our mission'}</Box>
            <Typography
              sx={{
                m: 0,
                fontFamily: DISPLAY,
                fontWeight: 700,
                fontSize: 'clamp(22px, 2.6vw, 34px)',
                lineHeight: 1.2,
                letterSpacing: '-.02em',
                textWrap: 'balance',
                color: 'var(--tl-g9)',
              }}
            >
              {story.mission
                || 'To take trusted testing to every doorstep, deliver clear answers in hours, and help people test before they need to.'}
            </Typography>
          </Box>
        </Box>

        {/* ── 5. the journey ─────────────────────────────────────────── */}
        <Box component="section" sx={{ ...SECTION_TOP, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, px: 1 }}>
            <Box component="span" sx={eyebrow}>Our journey</Box>
            <Typography component="h2" sx={h2}>From one lab to a national network</Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', maxWidth: 980 }}>
            {journey.map((e, i) => (
              <Box
                key={e._id || e.year}
                sx={{
                  display: 'grid',
                  // The year column collapses on a phone and the rail
                  // moves above the card, which keeps the text readable
                  // instead of squeezing it into a third of the screen.
                  gridTemplateColumns: {
                    xs: '28px minmax(0, 1fr)',
                    sm: 'clamp(84px, 12vw, 140px) 28px minmax(0, 1fr)',
                  },
                  gap: '0 16px',
                  alignItems: 'stretch',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: { xs: 'none', sm: 'block' },
                    pt: '22px',
                    textAlign: 'right',
                    fontFamily: DISPLAY,
                    fontWeight: 700,
                    fontSize: 'clamp(18px, 2.2vw, 26px)',
                    color: 'var(--tl-g9)',
                  }}
                >
                  {e.year}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Box component="span" sx={{ width: 3, height: 26, bgcolor: 'transparent' }} />
                  <Box
                    component="span"
                    sx={{
                      width: 18,
                      height: 18,
                      flexShrink: 0,
                      borderRadius: '50%',
                      bgcolor: 'var(--tl-g9)',
                      border: '4px solid var(--tl-mint)',
                      boxShadow: '0 0 0 2px var(--tl-g9)',
                    }}
                  />
                  {/* No tail under the last dot, or the line runs on
                      past the end of the story. */}
                  {i < journey.length - 1 && (
                    <Box component="span" sx={{ flex: 1, width: 3, bgcolor: '#BFE0D6' }} />
                  )}
                </Box>
                <Box sx={{ py: 1.5 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      p: '22px 24px',
                      borderRadius: '24px',
                      bgcolor: '#FFFFFF',
                      color: 'var(--tl-g9)',
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ display: { xs: 'block', sm: 'none' }, fontFamily: UI, fontSize: 13, fontWeight: 800, color: 'var(--tl-teal-d)' }}
                    >
                      {e.year}
                    </Box>
                    <Box component="span" sx={cardTitle}>{e.title}</Box>
                    <Box component="span" sx={{ fontFamily: UI, fontSize: 15, lineHeight: 1.6, color: 'var(--tl-muted)' }}>
                      {e.description}
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ── 6. what we stand for ───────────────────────────────────── */}
        <Box
          component="section"
          sx={{
            ...SECTION_TOP,
            display: 'flex',
            flexDirection: 'column',
            gap: 3.5,
            p: 'clamp(28px, 5vw, 56px)',
            borderRadius: '36px',
            bgcolor: 'var(--tl-g9)',
            color: '#FFFFFF',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <Box component="span" sx={{ ...eyebrow, color: 'var(--tl-teal-l)' }}>What we stand for</Box>
            <Typography component="h2" sx={{ ...h2, color: '#FFFFFF' }}>{sec.stands_for?.heading || 'Get it fast. Get it right.'}</Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap: 1.5 }}>
            {standsFor.map((c) => (
              <Box
                key={c.t}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.25,
                  p: '26px',
                  borderRadius: '24px',
                  bgcolor: c.accent ? 'var(--tl-yellow)' : 'var(--tl-g7)',
                  color: c.accent ? 'var(--tl-g9)' : '#FFFFFF',
                }}
              >
                <Box component="span" sx={cardTitle}>{c.t}</Box>
                <Box
                  component="span"
                  sx={{ fontFamily: UI, fontSize: 15, lineHeight: 1.6, color: c.accent ? '#3A4020' : 'var(--tl-line-2)' }}
                >
                  {c.d}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ── 7. departments ─────────────────────────────────────────── */}
        <Box component="section" sx={{ ...SECTION_TOP, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'end',
              justifyContent: 'space-between',
              gap: 2,
              px: 1,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Box component="span" sx={eyebrow}>{sec.departments?.eyebrow || 'Inside the lab'}</Box>
              <Typography component="h2" sx={h2}>{sec.departments?.heading || 'Our departments'}</Typography>
            </Box>
            <Box
              component={Link}
              to={ROUTES.TESTS}
              sx={{
                p: '11px 16px',
                borderRadius: '999px',
                bgcolor: '#FFFFFF',
                color: 'var(--tl-g9)',
                fontFamily: UI,
                fontSize: 15,
                fontWeight: 800,
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                '&:hover': { color: 'var(--tl-teal-d)' },
              }}
            >
              Browse all tests →
            </Box>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 240px), 1fr))', gap: 1.25 }}>
            {departments.map((d) => (
              <Box
                key={d.n}
                component="span"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: '18px 20px',
                  borderRadius: '20px',
                  bgcolor: d.lead ? 'var(--tl-g9)' : '#FFFFFF',
                  color: d.lead ? '#FFFFFF' : 'var(--tl-g9)',
                  fontFamily: UI,
                  fontSize: 16,
                  fontWeight: 800,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 10,
                    height: 10,
                    flexShrink: 0,
                    borderRadius: '50%',
                    bgcolor: d.lead ? 'var(--tl-teal-l)' : 'var(--tl-teal)',
                  }}
                />
                {d.n}
              </Box>
            ))}
          </Box>
        </Box>

        {/* ── 8. quality policy ──────────────────────────────────────── */}
        <Box
          component="section"
          sx={{
            ...SECTION_TOP,
            display: 'flex',
            flexDirection: 'column',
            gap: 3.5,
            p: 'clamp(28px, 5vw, 56px)',
            borderRadius: '36px',
            bgcolor: 'var(--tl-g9)',
            color: '#FFFFFF',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              gap: '24px 48px',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxWidth: 760 }}>
              <Box component="span" sx={{ ...eyebrow, color: 'var(--tl-teal-l)' }}>{qp.eyebrow || 'Quality policy'}</Box>
              <Typography component="h2" sx={{ ...h2, color: '#FFFFFF' }}>{qp.heading || 'Every result, done right.'}</Typography>
              <Typography sx={{ m: 0, fontFamily: UI, fontSize: 17, lineHeight: 1.7, color: 'var(--tl-line-2)' }}>
                {qp.intro
                  || 'TrustLab Diagnostics is committed to accurate, reliable and timely laboratory results that patients, doctors and partners can trust and act on. We operate a quality management system that complies with ISO 15189:2022 and NABL requirements, and we continually improve it. We commit to:'}
              </Typography>
            </Box>
            {qualityBadge ? (
              <Box
                component="span"
                sx={{
                  px: 2,
                  py: 1.25,
                  borderRadius: '999px',
                  bgcolor: 'var(--tl-yellow)',
                  color: 'var(--tl-g9)',
                  fontFamily: UI,
                  fontSize: 14,
                  fontWeight: 800,
                  whiteSpace: 'nowrap',
                }}
              >
                {qualityBadge}
              </Box>
            ) : null}
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap: 1.25 }}>
            {quality.map(({ t, d }) => (
              <Box
                key={t}
                sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: '22px', borderRadius: '22px', bgcolor: 'var(--tl-g7)' }}
              >
                <Box
                  component="span"
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.25, fontFamily: DISPLAY, fontWeight: 700, fontSize: 20 }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: 'grid',
                      placeItems: 'center',
                      width: 26,
                      height: 26,
                      flexShrink: 0,
                      borderRadius: '50%',
                      bgcolor: 'var(--tl-teal-l)',
                      color: 'var(--tl-g9)',
                      fontFamily: UI,
                      fontSize: 13,
                      fontWeight: 800,
                    }}
                  >
                    ✓
                  </Box>
                  {t}
                </Box>
                <Box component="span" sx={{ fontFamily: UI, fontSize: 15, lineHeight: 1.55, color: 'var(--tl-line-2)' }}>
                  {d}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ── 9. accreditations ──────────────────────────────────────── */}
        <Box component="section" sx={{ ...SECTION_TOP, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, px: 1 }}>
            <Box component="span" sx={eyebrow}>{sec.accreditations?.eyebrow || 'Quality'}</Box>
            <Typography component="h2" sx={h2}>{sec.accreditations?.heading || 'Accreditations and approvals'}</Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 1.5 }}>
            {accreditations.map(({ doc: plate, t, d, image_url: img }) => (
              <Box key={t} sx={panel}>
                {/* The scan once uploaded; the named plate until then, so
                    an un-uploaded certificate reads as pending rather
                    than broken. A PDF cannot render in an <img>, so it
                    keeps the plate and becomes a link. */}
                {img && !/\.pdf($|\?)/i.test(img) ? (
                  <Box
                    component="a"
                    href={img}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ display: 'block', borderRadius: '18px', overflow: 'hidden' }}
                  >
                    <Box
                      component="img"
                      src={img}
                      alt={`${t} certificate`}
                      sx={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', display: 'block' }}
                    />
                  </Box>
                ) : (
                  <Box
                    component={img ? 'a' : 'div'}
                    href={img || undefined}
                    target={img ? '_blank' : undefined}
                    rel={img ? 'noopener noreferrer' : undefined}
                    sx={{
                      aspectRatio: '4 / 3',
                      borderRadius: '18px',
                      bgcolor: 'var(--tl-mint)',
                      display: 'grid',
                      placeItems: 'center',
                      fontFamily: UI,
                      fontSize: 13,
                      fontWeight: 700,
                      color: 'var(--tl-teal-d)',
                      textDecoration: 'none',
                      textAlign: 'center',
                      p: 2,
                    }}
                  >
                    {img ? `${plate} (PDF)` : plate}
                  </Box>
                )}
                <Box component="span" sx={cardTitle}>{t}</Box>
                <Box component="span" sx={{ fontFamily: UI, fontSize: 15, lineHeight: 1.6, color: 'var(--tl-muted-2)' }}>
                  {d}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ── 10. leadership ─────────────────────────────────────────── */}
        {leaders.length > 0 && (
          <Box component="section" sx={{ ...SECTION_TOP, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, px: 1 }}>
              <Box component="span" sx={eyebrow}>Leadership</Box>
              <Typography component="h2" sx={h2}>The people behind TrustLab</Typography>
            </Box>
            {/* Centred rather than a left-packed grid: with two or three
                people an auto-fill grid leaves a wide empty gutter on the
                right. Flex lets the row sit in the middle and still fill
                out as more leaders are added. */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1.5 }}>
              {leaders.map((m) => (
                <Box
                  key={m._id || m.name}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.75,
                    p: 1.5,
                    borderRadius: '28px',
                    bgcolor: '#FFFFFF',
                    // Grows to fill a narrow screen, but never sprawls on a
                    // wide one — otherwise two cards would stretch halfway
                    // across the page each.
                    flex: '1 1 260px',
                    maxWidth: 320,
                    minWidth: 0,
                  }}
                >
                  {m.photo ? (
                    <Box
                      component="img"
                      src={m.photo}
                      alt={m.name}
                      sx={{
                        width: '100%',
                        aspectRatio: '1 / 1',
                        borderRadius: '20px',
                        // cover, not the reference's contain-on-black: its
                        // portrait is a tall transparent cut-out, while the
                        // admin uploads square headshots, which were coming
                        // out letterboxed with black bars either side.
                        objectFit: 'cover',
                        objectPosition: 'center top',
                        display: 'block',
                        bgcolor: '#D5EFE8',
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        aspectRatio: '1 / 1',
                        borderRadius: '20px',
                        bgcolor: '#D5EFE8',
                        display: 'grid',
                        placeItems: 'center',
                        fontFamily: DISPLAY,
                        fontSize: 40,
                        fontWeight: 700,
                        color: 'var(--tl-teal-d)',
                      }}
                    >
                      {m.initials || (m.name || '?').trim().charAt(0)}
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', p: '0 8px 10px' }}>
                    <Box component="span" sx={{ fontFamily: DISPLAY, fontSize: 21, fontWeight: 700 }}>{m.name}</Box>
                    <Box component="span" sx={{ fontFamily: UI, fontSize: 14, color: 'var(--tl-muted-2)' }}>{m.role}</Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* ── 11. our network ────────────────────────────────────────── */}
        <Box
          component="section"
          sx={{ ...SECTION_TOP, display: 'flex', flexWrap: 'wrap', borderRadius: '32px', overflow: 'hidden', bgcolor: '#FFFFFF' }}
        >
          <Box
            component="img"
            src={centreImg}
            alt="A TrustLab Care Centre"
            sx={{
              flex: '1 1 420px',
              minWidth: 0,
              width: '100%',
              maxHeight: 520,
              objectFit: 'cover',
              display: 'block',
            }}
          />
          <Box
            sx={{
              flex: '1 1 380px',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 2,
              p: 'clamp(24px, 4vw, 48px)',
            }}
          >
            <Box component="span" sx={eyebrow}>{sec.network?.eyebrow || 'Our network'}</Box>
            <Typography component="h2" sx={h2}>{sec.network?.heading || '14 branches and growing'}</Typography>
            <Typography sx={{ m: 0, fontFamily: UI, fontSize: 17, lineHeight: 1.7, color: '#3E5550' }}>
              Care Centres, regional labs and a home collection network that reach patients
              across six regions.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {regions.map((r) => (
                <Box
                  key={r}
                  component="span"
                  sx={{
                    p: '9px 14px',
                    borderRadius: '999px',
                    bgcolor: 'var(--tl-mint)',
                    fontFamily: UI,
                    fontSize: 14,
                    fontWeight: 700,
                  }}
                >
                  {r}
                </Box>
              ))}
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pt: 0.75 }}>
              {[
                { t: 'Find a branch', to: ROUTES.CENTRES, dark: true },
                { t: 'Open a Care Centre', to: ROUTES.FRANCHISE, dark: false },
              ].map((b) => (
                <Box
                  key={b.t}
                  component={Link}
                  to={b.to}
                  sx={{
                    p: '14px 20px',
                    borderRadius: '16px',
                    bgcolor: b.dark ? 'var(--tl-g9)' : 'var(--tl-yellow)',
                    color: 'var(--tl-g9)',
                    ...(b.dark ? { color: '#FFFFFF' } : null),
                    fontFamily: UI,
                    fontSize: 15,
                    fontWeight: 800,
                    textDecoration: 'none',
                    transition: `filter .25s ${EASE}`,
                    '&:hover': { filter: 'brightness(1.06)', color: b.dark ? '#FFFFFF' : 'var(--tl-g9)' },
                  }}
                >
                  {b.t}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* ── 12. visit us / work with us ────────────────────────────── */}
        <Box component="section" sx={{ ...SECTION_TOP, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          <Box
            sx={{
              flex: '1 1 380px',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.75,
              p: 'clamp(24px, 4vw, 40px)',
              borderRadius: '32px',
              bgcolor: 'var(--tl-teal)',
              color: '#FFFFFF',
            }}
          >
            <Box
              component="span"
              sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 'clamp(26px, 3.2vw, 40px)', letterSpacing: '-.02em', lineHeight: 1.05 }}
            >
              Visit or write to us
            </Box>
            <Box component="span" sx={{ fontFamily: UI, fontSize: 16, lineHeight: 1.7, color: 'var(--tl-teal-50)' }}>
              Central lab: {address}
            </Box>
            <Box
              component="a"
              href={`mailto:${email}`}
              sx={{
                alignSelf: 'start',
                p: '13px 18px',
                borderRadius: '999px',
                bgcolor: 'var(--tl-g9)',
                color: '#FFFFFF',
                fontFamily: UI,
                fontSize: 15,
                fontWeight: 800,
                textDecoration: 'none',
                '&:hover': { color: '#FFFFFF', bgcolor: 'var(--tl-g8)' },
              }}
            >
              {email}
            </Box>
          </Box>

          <Box sx={{ ...panel, flex: '1 1 380px', borderRadius: '28px', justifyContent: 'center' }}>
            <Box
              component="span"
              sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 'clamp(26px, 3.2vw, 40px)', letterSpacing: '-.02em', lineHeight: 1.05 }}
            >
              Work with us
            </Box>
            <Box component="span" sx={{ fontFamily: UI, fontSize: 16, lineHeight: 1.6, color: 'var(--tl-muted-2)' }}>
              Trustians, lab scientists, pathologists and care teams.
            </Box>
            <Box
              component={Link}
              to={ROUTES.CAREERS}
              sx={{
                alignSelf: 'start',
                p: '13px 18px',
                borderRadius: '999px',
                bgcolor: 'var(--tl-mint)',
                color: 'var(--tl-g9)',
                fontFamily: UI,
                fontSize: 15,
                fontWeight: 800,
                textDecoration: 'none',
                '&:hover': { bgcolor: 'var(--tl-mint-2)', color: 'var(--tl-g9)' },
              }}
            >
              See open roles →
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default About;
